import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const TIMEZONES = [
  { value: 'America/New_York', label: 'New York (EST)' },
  { value: 'America/Chicago', label: 'Chicago (CST)' },
  { value: 'America/Denver', label: 'Denver (MST)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST)' },
  { value: 'America/Anchorage', label: 'Anchorage (AKST)' },
  { value: 'Pacific/Honolulu', label: 'Honolulu (HST)' },
  { value: 'Europe/London', label: 'London, UK (GMT)' },
  { value: 'Europe/Dublin', label: 'Dublin, Ireland (GMT)' },
  { value: 'Europe/Lisbon', label: 'Lisbon, Portugal (WET)' },
  { value: 'Europe/Paris', label: 'Paris, France (CET)' },
  { value: 'Europe/Madrid', label: 'Madrid, Spain (CET)' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam, Netherlands (CET)' },
  { value: 'Europe/Brussels', label: 'Brussels, Belgium (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin, Germany (CET)' },
  { value: 'Europe/Zurich', label: 'Zurich, Switzerland (CET)' },
  { value: 'Europe/Vienna', label: 'Vienna, Austria (CET)' },
  { value: 'Europe/Rome', label: 'Rome, Italy (CET)' },
  { value: 'Europe/Prague', label: 'Prague, Czechia (CET)' },
  { value: 'Europe/Warsaw', label: 'Warsaw, Poland (CET)' },
  { value: 'Europe/Copenhagen', label: 'Copenhagen, Denmark (CET)' },
  { value: 'Europe/Stockholm', label: 'Stockholm, Sweden (CET)' },
  // Malmö shares Sweden's timezone — listed separately for naming flexibility
  { value: 'Europe/Stockholm', label: 'Malmö, Sweden (CET)' },
  { value: 'Europe/Oslo', label: 'Oslo, Norway (CET)' },
  { value: 'Europe/Helsinki', label: 'Helsinki, Finland (EET)' },
  { value: 'Europe/Athens', label: 'Athens, Greece (EET)' },
  { value: 'Europe/Moscow', label: 'Moscow, Russia (MSK)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'Mumbai (IST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEDT)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZDT)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
  { value: 'America/Mexico_City', label: 'Mexico City (CST)' },
  { value: 'America/Toronto', label: 'Toronto (EST)' },
  { value: 'Africa/Cairo', label: 'Cairo (EET)' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)' },
];

const AddClockSheet = ({ open, onOpenChange, onSubmit, editingClock }) => {
  const [customName, setCustomName] = useState('');
  const [city, setCity] = useState('');
  const [timezone, setTimezone] = useState('');
  const [memberName, setMemberName] = useState('');
  const [avatar, setAvatar] = useState('');
  const [workStartHour, setWorkStartHour] = useState(9);
  const [workEndHour, setWorkEndHour] = useState(19);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (editingClock) {
      setCustomName(editingClock.customName || '');
      setCity(editingClock.city || '');
      setTimezone(editingClock.timezone || '');
      setMemberName(editingClock.memberName || '');
      setAvatar(editingClock.avatar || '');
      setWorkStartHour(editingClock.workStartHour ?? 9);
      setWorkEndHour(editingClock.workEndHour ?? 19);
    } else {
      setCustomName('');
      setCity('');
      setTimezone('');
      setMemberName('');
      setAvatar('');
      setWorkStartHour(9);
      setWorkEndHour(19);
    }
  }, [editingClock, open]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        // Resize to 128x128 max to keep localStorage small
        const canvas = document.createElement('canvas');
        const size = 128;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        // Square crop from center
        const minDim = Math.min(img.width, img.height);
        const sx = (img.width - minDim) / 2;
        const sy = (img.height - minDim) / 2;
        ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, size, size);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        setAvatar(dataUrl);
        toast.success('Avatar uploaded');
      };
      img.onerror = () => toast.error('Could not load image');
      img.src = event.target.result;
    };
    reader.onerror = () => toast.error('Could not read file');
    reader.readAsDataURL(file);
  };

  const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: `${i.toString().padStart(2, '0')}:00`,
  }));

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!timezone) {
      toast.error('Please select a timezone');
      return;
    }

    if (!customName.trim()) {
      toast.error('Please enter a name for this clock');
      return;
    }

    if (workEndHour <= workStartHour) {
      toast.error('Work end hour must be after start hour');
      return;
    }

    onSubmit({
      timezone,
      city: city.trim() || customName.trim(),
      customName: customName.trim(),
      memberName: memberName.trim(),
      avatar: avatar || '',
      workStartHour,
      workEndHour,
    });

    onOpenChange(false);
    setCustomName('');
    setCity('');
    setTimezone('');
    setMemberName('');
    setAvatar('');
    setWorkStartHour(9);
    setWorkEndHour(19);
  };

  const handleCancel = () => {
    onOpenChange(false);
    setCustomName('');
    setCity('');
    setTimezone('');
    setMemberName('');
    setAvatar('');
    setWorkStartHour(9);
    setWorkEndHour(19);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="bg-[#050505] border-l border-white/[0.12] text-white w-full sm:max-w-md overflow-y-auto"
        data-testid="add-clock-sheet"
      >
        <SheetHeader>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#735AE2] mb-2">
            // {editingClock ? 'edit_clock.exe' : 'new_clock.exe'}
          </div>
          <SheetTitle className="font-heading text-2xl font-bold text-white">
            {editingClock ? 'Edit clock' : 'Add new clock'}
          </SheetTitle>
          <SheetDescription className="font-body text-zinc-400">
            {editingClock
              ? 'Update the clock details below'
              : 'Configure a new timezone clock for your dashboard'}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          {/* Avatar upload section */}
          <div className="space-y-2">
            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Team member avatar (optional)</Label>
            <div className="flex items-center gap-4">
              <div className="shrink-0">
                {avatar ? (
                  <img
                    src={avatar}
                    alt="Avatar preview"
                    className="w-16 h-16 object-cover border border-[#735AE2]/50"
                    data-testid="avatar-preview"
                  />
                ) : (
                  <div className="w-16 h-16 bg-white/[0.02] border border-white/[0.12] flex items-center justify-center">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      no img
                    </span>
                  </div>
                )}
              </div>
              <div className="flex-1 flex flex-col gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="avatar-file-input"
                />
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  className="bg-transparent border border-white/[0.15] text-white hover:bg-white/[0.05] hover:border-white/40 font-mono text-xs uppercase tracking-widest rounded-none"
                  data-testid="avatar-upload-btn"
                >
                  Upload image
                </Button>
                {avatar && (
                  <Button
                    type="button"
                    onClick={() => setAvatar('')}
                    variant="ghost"
                    className="text-zinc-400 hover:text-rose-400 font-mono text-[10px] uppercase tracking-widest rounded-none"
                    data-testid="avatar-remove-btn"
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="timezone" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              Timezone *
            </Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger
                id="timezone"
                data-testid="timezone-select"
                className="bg-white/[0.03] border-white/[0.15] text-white font-body focus:border-[#735AE2] focus:ring-0 rounded-none"
              >
                <SelectValue placeholder="Select a timezone" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/[0.15] text-white max-h-[300px] rounded-none">
                {TIMEZONES.map((tz) => (
                  <SelectItem
                    key={tz.label}
                    value={tz.value}
                    className="font-body focus:bg-[#735AE2]/20 focus:text-white rounded-none"
                  >
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="customName" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              Display name *
            </Label>
            <Input
              id="customName"
              data-testid="clock-name-input"
              placeholder="e.g., London Office, Marketing Team"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              className="bg-white/[0.03] border-white/[0.15] text-white placeholder:text-zinc-500 font-body focus:border-[#735AE2] focus-visible:ring-0 rounded-none"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="memberName" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              Team member name (optional)
            </Label>
            <Input
              id="memberName"
              data-testid="member-name-input"
              placeholder="e.g., Sarah Chen, John Smith"
              value={memberName}
              onChange={(e) => setMemberName(e.target.value)}
              className="bg-white/[0.03] border-white/[0.15] text-white placeholder:text-zinc-500 font-body focus:border-[#735AE2] focus-visible:ring-0 rounded-none"
            />
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              <span className="text-[#735AE2]">{'>'}</span> personalize the clock
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="city" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              City (optional)
            </Label>
            <Input
              id="city"
              data-testid="clock-city-input"
              placeholder="e.g., London, New York"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="bg-white/[0.03] border-white/[0.15] text-white placeholder:text-zinc-500 font-body focus:border-[#735AE2] focus-visible:ring-0 rounded-none"
            />
          </div>

          <div className="space-y-2">
            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Working hours</Label>
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 -mt-1 mb-2">
              <span className="text-[#735AE2]">{'>'}</span> defines this member's business hours
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="workStart" className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  Start
                </Label>
                <Select
                  value={workStartHour.toString()}
                  onValueChange={(v) => setWorkStartHour(parseInt(v, 10))}
                >
                  <SelectTrigger
                    id="workStart"
                    data-testid="work-start-select"
                    className="bg-white/[0.03] border-white/[0.15] text-white font-mono mt-1 focus:border-[#735AE2] focus:ring-0 rounded-none"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/[0.15] text-white max-h-[300px] rounded-none">
                    {HOUR_OPTIONS.map((opt) => (
                      <SelectItem
                        key={opt.value}
                        value={opt.value.toString()}
                        className="font-mono focus:bg-[#735AE2]/20 focus:text-white rounded-none"
                      >
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="workEnd" className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  End
                </Label>
                <Select
                  value={workEndHour.toString()}
                  onValueChange={(v) => setWorkEndHour(parseInt(v, 10))}
                >
                  <SelectTrigger
                    id="workEnd"
                    data-testid="work-end-select"
                    className="bg-white/[0.03] border-white/[0.15] text-white font-mono mt-1 focus:border-[#735AE2] focus:ring-0 rounded-none"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/[0.15] text-white max-h-[300px] rounded-none">
                    {HOUR_OPTIONS.map((opt) => (
                      <SelectItem
                        key={opt.value}
                        value={opt.value.toString()}
                        className="font-mono focus:bg-[#735AE2]/20 focus:text-white rounded-none"
                      >
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              data-testid="submit-clock-btn"
              className="flex-1 bg-[#735AE2] text-white hover:bg-[#8A75EA] rounded-none px-5 py-3 font-mono text-xs uppercase tracking-widest font-medium"
            >
              {editingClock ? 'Update clock' : 'Add clock'}
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="cancel-clock-btn"
              onClick={handleCancel}
              className="flex-1 bg-transparent border border-white/[0.2] text-white hover:bg-white/[0.05] rounded-none px-5 py-3 font-mono text-xs uppercase tracking-widest font-medium"
            >
              Cancel
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default AddClockSheet;
